// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: a small panel anchored to an element of the Plan view (a work chip, a coverage
// segment, an assignee row menu). One at a time; Escape, a click outside, a scroll or a
// resize closes it, and the focus goes back to the element that opened it.

import { h } from "./PMRender";

let current = null;

/** @dokos: close the open popover, if any. */
export function closePopover({ restoreFocus = true } = {}) {
	if (!current) return;
	const { node, anchor, cleanup, onClose } = current;
	current = null;
	cleanup();
	node.remove();
	onClose?.();
	if (restoreFocus && anchor?.isConnected) anchor.focus({ preventScroll: true });
}

/**
 * @dokos: open a popover under (or above) `anchor`.
 * @param {HTMLElement} anchor
 * @param {Object} opts
 * @param {string} opts.label accessible name of the dialog
 * @param {Array<Node|null>} opts.content
 * @param {string} [opts.className]
 * @param {() => void} [opts.onClose]
 * @returns {{node: HTMLElement, close: () => void}}
 */
export function openPopover(anchor, { label, content, className = "", onClose }) {
	const reopen = current?.anchor === anchor;
	closePopover({ restoreFocus: false });
	if (reopen) {
		// a second click on the same element closes it (toggle)
		anchor.focus({ preventScroll: true });
		return { node: null, close: () => {} };
	}

	const node = h(
		"div",
		{
			class: ["pm-popover", className],
			attrs: { role: "dialog", "aria-label": label, tabindex: "-1" },
		},
		...content
	);
	document.body.append(node);
	place(node, anchor);

	const onKey = (e) => {
		if (e.key === "Escape") {
			e.stopPropagation();
			closePopover();
		}
	};
	const onPointer = (e) => {
		if (!node.contains(e.target) && !anchor.contains(e.target) && !isInFloatingUi(e.target)) {
			closePopover({ restoreFocus: false });
		}
	};
	const onMove = (e) => {
		// the awesomplete list of a Link control scrolls inside the popover
		if (e?.target && node.contains(e.target)) return;
		closePopover({ restoreFocus: false });
	};
	node.addEventListener("keydown", onKey);
	document.addEventListener("mousedown", onPointer, true);
	window.addEventListener("scroll", onMove, true);
	window.addEventListener("resize", onMove);
	current = {
		node,
		anchor,
		onClose,
		cleanup: () => {
			document.removeEventListener("mousedown", onPointer, true);
			window.removeEventListener("scroll", onMove, true);
			window.removeEventListener("resize", onMove);
		},
	};

	(node.querySelector("button, [href], input, [tabindex]:not([tabindex='-1'])") || node).focus({
		preventScroll: true,
	});
	return { node, close: () => current?.node === node && closePopover() };
}

/** Below the anchor, else above it; kept inside the viewport. */
function place(node, anchor) {
	const margin = 8;
	const rect = anchor.getBoundingClientRect();
	const { innerWidth: width, innerHeight: height } = window;
	node.style.position = "fixed";
	node.style.left = "0px";
	node.style.top = "0px";
	const box = node.getBoundingClientRect();
	let left = Math.min(rect.left, width - box.width - margin);
	left = Math.max(margin, left);
	let top = rect.bottom + 4;
	if (top + box.height > height - margin && rect.top - box.height - 4 > margin) {
		top = rect.top - box.height - 4;
	}
	node.style.left = `${Math.round(left)}px`;
	node.style.top = `${Math.round(Math.max(margin, top))}px`;
}

/** The suggestions of a Link control and the desk dialogs live outside the popover. */
function isInFloatingUi(target) {
	return Boolean(target?.closest?.(".awesomplete, .modal, .es-toast, .alert-container"));
}

/**
 * @dokos: a row of the popover menu.
 * @param {Object} opts
 * @param {string} opts.label
 * @param {string} [opts.hint] right-aligned detail (counts, dates)
 * @param {boolean} [opts.danger]
 * @param {() => void} opts.action
 */
export function popoverItem({ label, hint, danger = false, action }) {
	return h(
		"button",
		{
			type: "button",
			class: ["pm-popover-item", danger && "is-danger"],
			on: {
				click: () => {
					closePopover({ restoreFocus: false });
					action();
				},
			},
		},
		h("span", { class: "pm-popover-item-label", text: label }),
		hint ? h("span", { class: "pm-popover-item-hint", text: hint }) : null
	);
}

/** @dokos: label / value line of a popover header. */
export function popoverFact(label, value) {
	return h(
		"div",
		{ class: "pm-popover-fact" },
		h("span", { class: "pm-popover-fact-label", text: label }),
		h("span", { class: "pm-popover-fact-value", text: value })
	);
}
